import React, { useMemo } from 'react';
import {
  useGetLocationAvailability,
  type MeetingLocations,
} from '@/api/useGetLocationAvailability';
import { Gmeet, Teams, Zoom } from '@/assets/icons/location';
import { Hackerrank } from '@/assets/icons/ats';
import { Phone } from '@kula-ai/icons/outline/Phone';
import { Building07 } from '@kula-ai/icons/outline/Building07';
import { CodeBrowser } from '@kula-ai/icons/outline/CodeBrowser';

type LocationValue = keyof MeetingLocations;

const LOCATION_OPTIONS: {
  value: LocationValue;
  label: string;
  icon: React.ComponentType<any>;
}[] = [
  { value: 'zoom', label: 'Zoom', icon: Zoom },
  { value: 'google_meet', label: 'Google Meet', icon: Gmeet },
  { value: 'teams', label: 'Teams', icon: Teams },
  { value: 'phone_interview', label: 'Phone Interview', icon: Phone },
  { value: 'hackerrank', label: 'HackerRank', icon: Hackerrank },
  { value: 'codeinterview', label: 'Code Interview', icon: CodeBrowser },
  { value: 'onsite', label: 'Onsite / Physical', icon: Building07 },
];

interface UseLocationFieldConfigProps {
  name?: string;
  label?: string;
  condition?: Record<string, unknown>;
  isHackerRank?: boolean;
  isCodeInterview?: boolean;
  config?: Record<string, unknown>;
  fieldProps?: Record<string, unknown>;
}

export function useLocationFieldConfig(props: UseLocationFieldConfigProps) {
  const { isHackerRank = false, isCodeInterview = false } = props;

  const { locations: availability } = useGetLocationAvailability();

  const options = useMemo(() => {
    // Don't show options until API has loaded — matches old behavior
    if (!availability) return [];

    return LOCATION_OPTIONS.filter((opt) =>
      Boolean(availability[opt.value])
    ).map((opt) => ({
      value: opt.value,
      label: opt.label,
      leftElement: React.createElement(opt.icon, { size: 16 }),
    }));
  }, [availability]);

  const defaultLocation = useMemo(() => {
    if (!options.length) return undefined;
    if (isHackerRank) return 'hackerrank';
    if (isCodeInterview) return 'codeinterview';
    const zoomAvailable = options.some((o) => o.value === 'zoom');
    return zoomAvailable ? 'zoom' : options[0]?.value;
  }, [options, isHackerRank, isCodeInterview]);

  const fieldConfig = useMemo(
    () => ({
      type: 'select' as const,
      name: 'location',
      label: 'Location',
      options,
      condition: props.condition,
      disabled: isHackerRank || isCodeInterview,
      ...(props.config || {}),
      fieldProps: { ...(props.fieldProps || {}) },
    }),
    [props, options, isHackerRank, isCodeInterview]
  );

  return { fieldConfig, options, defaultLocation };
}
